import React, {
  createContext,
  ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type GqlTrace, subscribeToGqlTraces } from '@/utils/graphqlDevTracer'

// ─── Cookie helpers ───────────────────────────────────────────────────────────

const GQL_ENABLED_COOKIE = 'dev_profiler_gql'
const GQL_FILTER_COOKIE = 'dev_profiler_gql_filter'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

function readCookie(key: string): string | undefined {
  return document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${key}=`))
    ?.split('=')[1]
}

function writeCookie(key: string, value: string): void {
  document.cookie = `${key}=${value}; path=/; max-age=${COOKIE_MAX_AGE}`
}

function readGqlEnabledCookie(): boolean {
  return readCookie(GQL_ENABLED_COOKIE) === 'true'
}

function readGqlFilterCookie(): string {
  return decodeURIComponent(readCookie(GQL_FILTER_COOKIE) ?? '')
}

// ─── Entry types ──────────────────────────────────────────────────────────────

export interface ManualEntry {
  kind: 'manual'
  id: string
  label: string
  value: unknown
  timestamp: string
}

export interface GqlEntry extends GqlTrace {
  kind: 'gql'
}

export type DumpEntry = ManualEntry | GqlEntry

// ─── Context ──────────────────────────────────────────────────────────────────

interface DevProfilerContextValue {
  entries: DumpEntry[]
  dump: (label: string, value: unknown) => void
  remove: (id: string) => void
  clear: () => void
  gqlEnabled: boolean
  setGqlEnabled: (value: boolean) => void
  gqlFilter: string
  setGqlFilter: (value: string) => void
}

const DevProfilerContext = createContext<DevProfilerContextValue | undefined>(
  undefined
)

// ─── Provider ─────────────────────────────────────────────────────────────────

export const DevProfilerProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [entries, setEntries] = useState<DumpEntry[]>([])
  const [gqlEnabled, setGqlEnabledState] = useState<boolean>(() =>
    readGqlEnabledCookie()
  )
  const [gqlFilter, setGqlFilterState] = useState<string>(() =>
    readGqlFilterCookie()
  )

  const gqlEnabledRef = useRef(gqlEnabled)
  gqlEnabledRef.current = gqlEnabled

  const setGqlEnabled = useCallback((value: boolean) => {
    setGqlEnabledState(value)
    writeCookie(GQL_ENABLED_COOKIE, String(value))
  }, [])

  const setGqlFilter = useCallback((value: string) => {
    setGqlFilterState(value)
    writeCookie(GQL_FILTER_COOKIE, encodeURIComponent(value))
  }, [])

  const dump = useCallback((label: string, value: unknown) => {
    setEntries((prev) => [
      {
        kind: 'manual',
        id: crypto.randomUUID(),
        label,
        value,
        timestamp: new Date().toISOString(),
      },
      ...prev,
    ])
  }, [])

  const remove = useCallback(
    (id: string) => setEntries((prev) => prev.filter((e) => e.id !== id)),
    []
  )

  const clear = useCallback(() => setEntries([]), [])

  // Subscribe to Apollo link traces — only active in DEV (the tracer itself is
  // a no-op when no link emits). Checks gqlEnabledRef so the callback always
  // reads the latest toggle state without needing to re-subscribe.
  useEffect(() => {
    return subscribeToGqlTraces((trace) => {
      if (!gqlEnabledRef.current) return
      setEntries((prev) => [{ kind: 'gql', ...trace }, ...prev])
    })
  }, [])

  const value = useMemo(
    () => ({
      entries,
      dump,
      remove,
      clear,
      gqlEnabled,
      setGqlEnabled,
      gqlFilter,
      setGqlFilter,
    }),
    [
      entries,
      dump,
      remove,
      clear,
      gqlEnabled,
      setGqlEnabled,
      gqlFilter,
      setGqlFilter,
    ]
  )

  return (
    <DevProfilerContext.Provider value={value}>
      {children}
    </DevProfilerContext.Provider>
  )
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

export function useDevProfilerContext(): DevProfilerContextValue {
  const ctx = use(DevProfilerContext)
  if (!ctx) {
    throw new Error(
      'useDevProfilerContext must be used within DevProfilerProvider'
    )
  }
  return ctx
}

const noopDump = () => {}

/**
 * Public hook: exposes only `dump` to keep consumers decoupled from the
 * profiler state. Safe to call outside the provider (no-op in that case).
 */
export function useDevProfiler(): {
  dump: (label: string, value: unknown) => void
} {
  const ctx = use(DevProfilerContext)
  return { dump: ctx?.dump ?? noopDump }
}
